import React from 'react'
import cx from 'classnames'
import dayjs from 'dayjs'
import get from 'lodash/get'
import omit from 'lodash/omit'
import { DatePicker, l, Icon, Checkbox, SearchInput } from '@manychat/manyui'

import FlowTextEditor from 'common/builder/components/misc/FlowTextEditor'
import WeekDaySelect from 'common/core/components/WeekDaySelect/WeekDaySelect'
import FormulaInput from 'common/fields/components/FormulaInput'
import { FieldType } from 'common/fields/entities/enums'
import UserFieldPicker from 'common/fields/linking/components/UserFieldPicker'
import FieldValuesController from 'common/filter/controllers/FieldValues'
import {
  UserFieldDateTypes,
  UserFieldDateOptions,
  OffsetOperations,
  DATE_FORMULA,
  DATE_TIME_FORMULA,
  InputDisplayType,
  FilterField,
} from 'common/filter/models/AudienceFilter/constants'
import {
  prepareDateTimeWithBotTimezoneForBackend,
  parseDateTimeWithBotTimezoneFromBackend,
} from 'common/usesList/utils'
import { SECONDS_IN_DAY } from 'components/DelayPicker/constants'
import { DelayPicker } from 'components/DelayPicker/DelayPicker'
import { Select } from 'components/forms/Select/Select'
import { VAR_REGEX } from 'constants/RegExs'

import { FieldSegmentType } from '../../../../fields/entities/enums'
import { ConditionTagPicker } from '../../../../tags/components/ConditionTagPicker/ConditionTagPicker'

import FieldValuePicker from './FieldValuePicker'
import TextAutocomplete from './TextAutocomplete'

import cm from './ConditionValue.module.css'

/**
 * Get field data type from CUF/SUF/GUF.
 * @param {string} type
 * @returns {string}
 */
function getFieldType(type = '') {
  if (type === FilterField.CURRENT_DATETIME) {
    return FieldType.DATETIME
  }

  return type.slice(type.indexOf('_') + 1)
}

class ConditionValue extends React.Component {
  handleCurrentTimeFromChange = (e) => {
    const { value: currentValue } = this.props.condition
    const value = {
      ...omit(currentValue, 'time_zone'),
      from: { time: e.target.value },
    }
    this.props.onChange({ value })
  }

  handleCurrentTimeToChange = (e) => {
    const { value: currentValue } = this.props.condition
    const value = {
      ...omit(currentValue, 'time_zone'),
      to: { time: e.target.value },
    }
    this.props.onChange({ value })
  }

  handleCurrentTimeWeekdaysChange = (e) => {
    const { value } = this.props.condition
    value.weekdays = e.target.value

    this.props.onChange({ value })
  }

  render() {
    const { fieldDesc, operatorDesc, condition } = this.props
    switch (operatorDesc.display) {
      case InputDisplayType.NONE:
        return null
      case InputDisplayType.TEXT_INPUT:
        return this.renderTextInput()
      case InputDisplayType.NUMBER:
        return this.renderNumberInput()
      case InputDisplayType.FIELD_VALUE_PICKER:
        return this.renderFieldValuePicker()
      case InputDisplayType.DATEPICKER: {
        return this.renderDateControl()
      }
      case InputDisplayType.DELAY: {
        return this.renderDelay()
      }
      case InputDisplayType.SYSTEM_CURRENT_DATETIME_BETWEEN: {
        return this.renderTimeBetween()
      }
    }

    return (
      <div>
        <p>Field type: {fieldDesc.type}</p>
        <p>Value: {condition.value}</p>
        <p>Operator: {condition.operator}</p>
      </div>
    )
  }

  renderTimeBetween() {
    const { botTimezone } = this.props
    const { value } = this.props.condition
    const { from, to, weekdays, time_zone: timeZone } = value

    const botTZ = botTimezone ? dayjs().tz(botTimezone).format('Z') : null
    const utc = timeZone ? `${timeZone}:00` : botTZ

    return (
      <div className="p-a" style={{ width: '260px' }}>
        <div className="d-flex">
          <div className="">
            {l.translate('From:')}
            <DatePicker
              value={from.time}
              onChange={this.handleCurrentTimeFromChange}
              type={'time'}
            />
          </div>
          <div className="m-l">
            {l.translate('To:')}
            <DatePicker value={to.time} onChange={this.handleCurrentTimeToChange} type={'time'} />
          </div>
        </div>
        <div className="m-t">
          {l.translate('Days:')}
          <WeekDaySelect
            name="weekdays"
            value={weekdays}
            onChange={this.handleCurrentTimeWeekdaysChange}
            oneOrMore
          />
        </div>
        <div className="m-t text-secondary">
          {l.translate('The time is shown in UTC {utc}', { utc })}
        </div>
      </div>
    )
  }

  renderTextInput() {
    const { condition, fieldDesc, isBuilder } = this.props

    return (
      <>
        {isBuilder ? (
          <div className="p-a" style={{ width: '200px' }}>
            <FlowTextEditor
              onChange={this.handleChange}
              value={condition.value ?? ''}
              minRows={1}
              maxRows={6}
              placeholder={l.translate('Enter text')}
              isValid
              withDefaultStyles
            />
          </div>
        ) : (
          <FieldValuesController fieldDesc={fieldDesc} value={condition.value}>
            <TextAutocomplete
              value={condition.value || ''}
              onChange={this.handleChange}
              placeholder={l.translate('Enter text')}
            />
          </FieldValuesController>
        )}
        {isBuilder && (
          <Checkbox
            label={l.translate('Case Sensitivity')}
            checked={condition.case_sensitive}
            onChange={this.handleSensitivityChange}
            className={'d-flex align-center m-l'}
            infoText={l.translate(
              'Leave the box unchecked to interpret upper and lowercase letters as being the same.',
            )}
          />
        )}
      </>
    )
  }

  renderNumberInput() {
    const { condition, isBuilder } = this.props

    return isBuilder ? (
      <div className="p-a">
        <FormulaInput
          value={condition.value || ''}
          onChange={this.handleChange}
          placeholder={l.translate('Enter formula')}
          errorsClassName={cm.formulaErrors}
        />
      </div>
    ) : (
      <SearchInput
        value={condition.value || ''}
        onChange={this.handleChange}
        placeholder={l.translate('Enter text')}
        ariaLabel={l.translate('Enter text')}
      />
    )
  }

  renderDatePicker(disableTime) {
    const { condition, botTimezone } = this.props

    return (
      <div>
        <DatePicker
          value={
            botTimezone && !disableTime
              ? parseDateTimeWithBotTimezoneFromBackend(condition.value, botTimezone)
              : condition.value || ''
          }
          onChange={botTimezone && !disableTime ? this.handleChangeDatePicker : this.handleChange}
          type={disableTime ? 'date' : 'datetime'}
        />
      </div>
    )
  }

  renderDelay() {
    const { condition } = this.props
    return (
      <div className="p-a row middle">
        <DelayPicker
          className={cm.delayPicker}
          delay={Math.abs(condition.value) || SECONDS_IN_DAY}
          onChange={this.handleDelayChange}
          minutes
          hours
          days
        />
        <div className="m-l-xs">{l.translate('ago')}</div>
      </div>
    )
  }

  renderFieldValuePicker() {
    const { condition, fieldDesc } = this.props
    return condition?.type === FieldSegmentType.TAG ? (
      <ConditionTagPicker
        condition={condition}
        onChange={this.handleChange}
        fieldDescription={fieldDesc}
      />
    ) : (
      <FieldValuesController fieldDesc={fieldDesc}>
        <FieldValuePicker condition={condition} onChange={this.handleChange} />
      </FieldValuesController>
    )
  }

  handleBrowseCatalogClick = () => {}

  handleSensitivityChange = (e) => {
    this.props.onChange({ case_sensitive: e.target.checked })
  }

  handleDelayChange = (value) => {
    return this.props.onChange({
      value: -1 * value,
      unit: 'seconds',
    })
  }

  handleDelayOffsetChange = (value) => {
    const { onChange, condition } = this.props
    return onChange({
      offset: {
        ...condition.offset,
        value,
      },
    })
  }

  setDefaultOffset = () => {
    this.props.onChange(
      {
        offset: {
          operator: '+',
          value: SECONDS_IN_DAY,
        },
      },
      false,
    )
  }

  resetOffset = () => {
    this.props.onChange({ offset: null }, false)
  }

  handleChange = (e, closePopover) => {
    return this.props.onChange({ value: e.target.value }, closePopover)
  }

  handleChangeDatePicker = (e, closePopover) => {
    const { botTimezone } = this.props

    return this.props.onChange(
      {
        value: botTimezone
          ? prepareDateTimeWithBotTimezoneForBackend(e.target.value, botTimezone)
          : e.target.value,
      },
      closePopover,
    )
  }

  handleDelayCheckBoxChange = ({ target }) => {
    const { checked } = target
    checked ? this.setDefaultOffset() : this.resetOffset()
  }

  exportFormula = (target) => {
    let tmp = ''
    const type = get(target, 'field.type', null)
    const value = get(target, 'value', null)

    if (value) {
      if (type === 'datetime') {
        tmp = DATE_TIME_FORMULA
      } else if (type === 'date') {
        tmp = DATE_FORMULA
      }
      return `${tmp}({{cuf_${value}}})`
    }
  }

  parseValue = () => {
    const { condition } = this.props
    const isFormula = condition[UserFieldDateTypes.FORMULA]
    let value = condition.value
    if (isFormula && value) {
      const parsing = VAR_REGEX.exec(condition.value)
      if (parsing) {
        value = parsing[1].replace('cuf_', '')
      }
    }

    return parseInt(value, 10) || null
  }

  handleComparableUserFieldChange = ({ target }) => {
    const value = this.exportFormula(target)
    this.props.onChange({ value }, false)
  }

  renderLabel = (label) => <label className="text-secondary">{label}</label>

  renderDateFormula = () => {
    const {
      fieldDesc: { type: targetFieldType },
      condition,
    } = this.props

    return (
      <>
        {this.renderLabel('Custom Field')}
        <div className={cx('m-t-xxs m-b-md', cm.userFieldPickerWrapper)}>
          <UserFieldPicker
            allowedTypes={[getFieldType(targetFieldType)]}
            name="date_field_id"
            onChange={this.handleComparableUserFieldChange}
            value={this.parseValue()}
          />
        </div>
        <div className="d-flex align-center m-t-xxs">
          <Checkbox
            name="delay"
            checked={Boolean(condition.offset)}
            disabled={Boolean(this.parseValue())}
            onChange={this.handleDelayCheckBoxChange}
            label="Offset"
          />
          <Icon.Question
            size={16}
            className="m-l-xxs pointer"
            data-title-at="right"
            data-title="Offset option helps you to shift date/time from Custom Field by a certain value"
          />
        </div>
        {condition.offset && (
          <div className="d-flex align-center m-t-md">{this.renderOffsetControl()}</div>
        )}
      </>
    )
  }

  renderDateControl = () => {
    const { condition, fieldDesc, allowDateFormula } = this.props
    const isFormula = condition[UserFieldDateTypes.FORMULA]

    return (
      <div className="p-a">
        {this.renderLabel('Type')}
        {allowDateFormula && (
          <>
            {this.renderDateTypeSelect()}
            {isFormula && this.renderDateFormula()}
          </>
        )}
        {!isFormula &&
          this.renderDatePicker(fieldDesc.type === 'date' || fieldDesc.id === 'subscribed')}
      </div>
    )
  }

  handleDateTypeChange = (e, closePopover) => {
    const { value } = e.target
    this.props.onChange(
      { [UserFieldDateTypes.FORMULA]: value === UserFieldDateTypes.FORMULA },
      closePopover,
    )
  }

  handleOffsetOperatorChange = ({ target }) => {
    const {
      condition: { offset },
    } = this.props
    const { value } = target

    this.props.onChange(
      {
        offset: {
          ...offset,
          operator: value,
        },
      },
      false,
    )
  }

  renderDateTypeSelect = () => {
    const { condition } = this.props
    const isFormula = get(condition, UserFieldDateTypes.FORMULA, false)
    const type = isFormula ? UserFieldDateTypes.FORMULA : UserFieldDateTypes.SET

    return (
      <Select
        key="date-type-select"
        className="m-t-xxs m-b-md"
        id="create-cuf-type"
        options={UserFieldDateOptions}
        value={type}
        onChange={this.handleDateTypeChange}
      />
    )
  }

  renderOffsetControl = () => {
    const { condition, fieldDesc: targetField } = this.props
    const { offset } = condition
    const offsetValue =
      Math.abs(condition.offset.value) || Math.abs(condition.value) || SECONDS_IN_DAY
    const offsetUnits = { days: true }

    if (getFieldType(targetField.type) === 'datetime') {
      offsetUnits.hours = true
      offsetUnits.minutes = true
    }

    return (
      <>
        <Select
          className="m-r-xs"
          id="select-offset-operator"
          options={OffsetOperations}
          onChange={this.handleOffsetOperatorChange}
          value={offset.operator}
        />

        <DelayPicker
          className={cm.delayPicker}
          delay={offsetValue}
          onChange={this.handleDelayOffsetChange}
          {...offsetUnits}
        />
      </>
    )
  }
}

export default ConditionValue
